import React, { useState } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Slide
} from '@mui/material';
import {
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ArrowBackIos as ArrowLeftIcon,
  ArrowForwardIos as ArrowRightIcon
} from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PhotoGalleryModal = ({ open, onClose, images, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  if (!images || images.length === 0) return null;

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: 'black',
          margin: 0,
          padding: 0,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header with controls */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: isMobile ? 1.5 : 2
      }}>
        {/* Empty space - left */}
        <Box sx={{ width: isMobile ? 8 : 48 }} />

        {/* Title in center */}
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 500,
            textAlign: 'center',
            flex: 1,
            mx: isMobile ? 1 : 2,
            fontSize: isMobile ? '14px' : '20px',
            lineHeight: 1.3,
            textShadow: '0 1px 3px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {title} - {currentImageIndex + 1} z {images.length}
        </Typography>

        {/* Right buttons group */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Favorite button */}
          <IconButton
            onClick={toggleFavorite}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              width: isMobile ? 36 : 48,
              height: isMobile ? 36 : 48,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            {isFavorite ? <FavoriteIcon sx={{ color: '#ff4757', fontSize: isMobile ? 20 : 24 }} /> : <FavoriteBorderIcon sx={{ fontSize: isMobile ? 20 : 24 }} />}
          </IconButton>

          {/* Close button - right */}
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              width: isMobile ? 40 : 58,
              height: isMobile ? 40 : 58,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: isMobile ? 22 : 28 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Main image area */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Main image */}
        <Box sx={{
          position: 'absolute',
          top: 60, // Below header
          bottom: isMobile ? 120 : 140, // Above thumbnail carousel
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}>
          <img
            src={images[currentImageIndex]}
            alt={`Zdjęcie ${currentImageIndex + 1}`}
            style={{
              width: '90vw',
              height: '70vh',
              objectFit: 'contain',
              userSelect: 'none'
            }}
          />

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: isMobile ? 8 : 20,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  }
                }}
              >
                <ArrowLeftIcon sx={{ fontSize: isMobile ? 18 : 24 }} />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: isMobile ? 8 : 20,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  }
                }}
              >
                <ArrowRightIcon sx={{ fontSize: isMobile ? 18 : 24 }} />
              </IconButton>
            </>
          )}
        </Box>

        {/* Thumbnail carousel at bottom */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
          p: 2,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Box sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            maxWidth: '100%',
            pb: 1,
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: 3,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              }
            }
          }}>
            {images.map((image, index) => (
              <Box
                key={index}
                onClick={() => handleThumbnailClick(index)}
                sx={{
                  minWidth: isMobile ? 60 : 80,
                  height: isMobile ? 60 : 80,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: currentImageIndex === index 
                    ? '2px solid #fff' 
                    : '2px solid transparent',
                  opacity: currentImageIndex === index ? 1 : 0.7,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <img
                  src={image}
                  alt={`Miniatura ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Keyboard navigation */}
      <Box
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') handlePrevious();
          if (e.key === 'ArrowRight') handleNext();
          if (e.key === 'Escape') onClose();
        }}
        tabIndex={0}
        sx={{ outline: 'none' }}
      />
    </Dialog>
  );
};

export default PhotoGalleryModal;